<script>
import BranchAdminDialog from './BranchAdminDialog.vue'
import {
  getBranchDetail,
  updateBranch,
  deleteBranch,
  deleteBranchAdmin,
  updateBranchAdminPassword,
  createFeePolicy,
  updateFeePolicy,
  deleteFeePolicy,
} from '@/api/branch'
import { getAdminInfo } from '@/utils/token'

/**
 * 지점 상세 페이지.
 *
 * 구성:
 *  - 헤더: 지점명, 구분/상태 배지, 뒤로가기
 *  - 탭 1: 지점 기본정보 (HQ만 수정, 지점관리자는 읽기 전용)
 *  - 탭 2: 지점관리자 테이블 (추가/수정/삭제/비밀번호변경)
 *  - 탭 3: 수수료 정책 테이블 (HQ만 추가/수정/삭제, 지점관리자는 조회만)
 *  - 하단: 지점 삭제 (HQ 전용)
 *
 * 권한 정책:
 *  - HQ: 기본정보 수정, 관리자 CRUD, 수수료 정책 CRUD, 지점 삭제 가능
 *  - 지점관리자: 기본정보 조회만, 관리자 CRUD(본인 지점), 수수료 정책 조회만
 */
export default {
  name: 'BranchDetailPage',
  components: { BranchAdminDialog },
  data() {
    return {
      currentAdmin: getAdminInfo(),

      /** 지점 상세 데이터 (API 응답 원본) */
      detail: null,
      loading: true,

      /* ===== 지점 기본정보 수정 폼 ===== */
      branchForm: {
        branchName: '',
        branchNo: '',
        branchType: 'DIRECT',
        branchStatus: 'ACTIVE',
        zipcode: '',
        address1: '',
        address2: '',
        locationX: '',
        locationY: '',
      },
      branchSaving: false,
      branchSaveMsg: '',
      branchSaveError: '',

      branchTypeOptions: [
        { title: '직영점', value: 'DIRECT' },
        { title: '대리점', value: 'AGENCY' },
        { title: '가맹점', value: 'FRANCHISE' },
      ],
      branchStatusOptions: [
        { title: '운영중', value: 'ACTIVE' },
        { title: '일시중지', value: 'INACTIVE' },
        { title: '폐점', value: 'CLOSED' },
      ],

      /* ===== 관리자 다이얼로그 ===== */
      adminDialog: false,
      adminFormMode: 'create',
      selectedAdmin: null,

      /* ===== 관리자 삭제 확인 ===== */
      adminDeleteDialog: false,
      adminDeleteLoading: false,
      adminToDelete: null,

      /* ===== 관리자 비밀번호 변경 ===== */
      pwdDialog: false,
      pwdSaving: false,
      pwdForm: { newPassword: '', confirmPassword: '' },
      pwdTarget: null,
      pwdError: '',
      pwdMsg: '',

      /* ===== 수수료 정책 다이얼로그 ===== */
      feeDialog: false,
      feeFormMode: 'create',
      feeSaving: false,
      feeForm: { buyFeeRate: '', sellFeeRate: '', startDate: '', endDate: '' },
      feeToEdit: null,
      feeSaveError: '',

      /* ===== 수수료 정책 삭제 확인 ===== */
      feeDeleteDialog: false,
      feeToDelete: null,
      feeDeleteLoading: false,

      /* ===== 지점 삭제 확인 ===== */
      branchDeleteDialog: false,
      branchDeleteLoading: false,

      /* ===== 탭 ===== */
      activeTab: 'info',
    }
  },
  computed: {
    branchCode() {
      return this.$route.params.branchCode
    },
    isHq() {
      return this.currentAdmin?.adminType === 'HQ'
    },
  },
  mounted() {
    this.fetchDetail()
  },
  methods: {
    /* ================================================================
     * 지점 상세 조회
     * ================================================================ */

    async fetchDetail() {
      this.loading = true
      try {
        const res = await getBranchDetail(this.branchCode)
        if (res.data.code === 0) {
          this.detail = res.data
          this.initBranchForm()
        }
      } finally {
        this.loading = false
      }
    },

    /** 지점 폼을 상세 데이터로 초기화 */
    initBranchForm() {
      if (!this.detail) return
      this.branchForm = {
        branchName: this.detail.branchName || '',
        branchNo: this.formatBranchNo(this.detail.branchNo || ''),
        branchType: this.detail.branchType || 'DIRECT',
        branchStatus: this.detail.branchStatus || 'ACTIVE',
        zipcode: this.detail.zipcode || '',
        address1: this.detail.address1 || '',
        address2: this.detail.address2 || '',
        locationX: this.detail.locationX || '',
        locationY: this.detail.locationY || '',
      }
      this.branchSaveMsg = ''
      this.branchSaveError = ''
    },

    /* ================================================================
     * 주소 검색 (다음 우편번호 서비스)
     * ================================================================ */

    /** 다음 주소찾기 팝업 오픈 - 선택 완료 시 주소/좌표 자동 입력 */
    openAddressSearch() {
      if (!window.daum?.Postcode) {
        this.branchSaveError = '주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.'
        return
      }
      new window.daum.Postcode({
        oncomplete: (data) => {
          const address = data.roadAddress || data.autoRoadAddress || data.jibunAddress
          this.branchForm.zipcode = data.zonecode
          this.branchForm.address1 = address
          // Daum Postcode v2: x = 경도(longitude), y = 위도(latitude)
          this.branchForm.locationX = data.x || ''
          this.branchForm.locationY = data.y || ''
        },
      }).open()
    },

    /* ================================================================
     * 지점 기본정보 저장
     * ================================================================ */

    async handleBranchSave() {
      this.branchSaveMsg = ''
      this.branchSaveError = ''
      if (!this.branchForm.branchName?.trim()) {
        this.branchSaveError = '지점명은 필수입니다.'
        return
      }
      this.branchSaving = true
      try {
        const res = await updateBranch(this.branchCode, {
          ...this.branchForm,
          branchNo: this.branchForm.branchNo.replace(/-/g, ''),
        })
        if (res.data.code !== 0) {
          this.branchSaveError = res.data.message || '저장에 실패했습니다.'
          return
        }
        this.branchSaveMsg = '저장되었습니다.'
        await this.fetchDetail()
        setTimeout(() => { this.branchSaveMsg = '' }, 3000)
      } catch (e) {
        this.branchSaveError = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.branchSaving = false
      }
    },

    /* ================================================================
     * 관리자 CRUD
     * ================================================================ */

    openAdminCreate() {
      this.adminFormMode = 'create'
      this.selectedAdmin = null
      this.adminDialog = true
    },

    openAdminEdit(admin) {
      this.adminFormMode = 'edit'
      this.selectedAdmin = admin
      this.adminDialog = true
    },

    openAdminDelete(admin) {
      this.adminToDelete = admin
      this.adminDeleteDialog = true
    },

    async handleAdminDelete() {
      this.adminDeleteLoading = true
      try {
        const res = await deleteBranchAdmin(this.branchCode, this.adminToDelete.branchAdminCode)
        if (res.data.code === 0) {
          this.adminDeleteDialog = false
          await this.fetchDetail()
        }
      } finally {
        this.adminDeleteLoading = false
      }
    },

    async onAdminSaved() {
      await this.fetchDetail()
    },

    /* ================================================================
     * 관리자 비밀번호 변경
     * ================================================================ */

    /** 비밀번호 변경 다이얼로그 오픈 */
    openPwdChange(admin) {
      this.pwdTarget = admin
      this.pwdForm = { newPassword: '', confirmPassword: '' }
      this.pwdError = ''
      this.pwdMsg = ''
      this.pwdDialog = true
    },

    /** 비밀번호 변경 저장 처리 */
    async handlePwdSave() {
      this.pwdError = ''
      this.pwdMsg = ''

      // 클라이언트 유효성 검사
      if (!this.pwdForm.newPassword || this.pwdForm.newPassword.length < 8) {
        this.pwdError = '비밀번호는 8자 이상이어야 합니다.'
        return
      }
      if (this.pwdForm.newPassword !== this.pwdForm.confirmPassword) {
        this.pwdError = '새 비밀번호와 확인 비밀번호가 일치하지 않습니다.'
        return
      }

      this.pwdSaving = true
      try {
        const res = await updateBranchAdminPassword(
          this.branchCode,
          this.pwdTarget.branchAdminCode,
          { newPassword: this.pwdForm.newPassword }
        )
        if (res.data.code === 0) {
          this.pwdMsg = '비밀번호가 변경되었습니다.'
          setTimeout(() => { this.pwdDialog = false }, 1200)
        } else {
          this.pwdError = res.data.message || '비밀번호 변경에 실패했습니다.'
        }
      } catch (e) {
        this.pwdError = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.pwdSaving = false
      }
    },

    /* ================================================================
     * 수수료 정책 CRUD
     * ================================================================ */

    openFeeCreate() {
      this.feeFormMode = 'create'
      this.feeToEdit = null
      this.feeForm = { buyFeeRate: '', sellFeeRate: '', startDate: '', endDate: '' }
      this.feeSaveError = ''
      this.feeDialog = true
    },

    openFeeEdit(policy) {
      this.feeFormMode = 'edit'
      this.feeToEdit = policy
      this.feeForm = {
        buyFeeRate: policy.buyFeeRate,
        sellFeeRate: policy.sellFeeRate,
        startDate: policy.startDate ? policy.startDate.slice(0, 16) : '',
        endDate: policy.endDate ? policy.endDate.slice(0, 16) : '',
      }
      this.feeSaveError = ''
      this.feeDialog = true
    },

    async handleFeeSave() {
      this.feeSaveError = ''
      this.feeSaving = true
      try {
        // 적용 종료일 미입력 시 3000-12-31로 기본 설정
        // 백엔드에서도 동일하게 처리하므로 이중 방어
        const endDate = this.feeForm.endDate || '3000-12-31T00:00'
        const payload = {
          buyFeeRate: parseFloat(this.feeForm.buyFeeRate),
          sellFeeRate: parseFloat(this.feeForm.sellFeeRate) || 0,
          startDate: this.feeForm.startDate || null,
          endDate,
        }
        let res
        if (this.feeFormMode === 'create') {
          res = await createFeePolicy(this.branchCode, payload)
        } else {
          res = await updateFeePolicy(this.branchCode, this.feeToEdit.policyId, payload)
        }
        if (res.data.code === 0) {
          this.feeDialog = false
          await this.fetchDetail()
        } else {
          this.feeSaveError = res.data.message || '저장에 실패했습니다.'
        }
      } catch (e) {
        this.feeSaveError = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.feeSaving = false
      }
    },

    openFeeDelete(policy) {
      this.feeToDelete = policy
      this.feeDeleteDialog = true
    },

    async handleFeeDelete() {
      this.feeDeleteLoading = true
      try {
        const res = await deleteFeePolicy(this.branchCode, this.feeToDelete.policyId)
        if (res.data.code === 0) {
          this.feeDeleteDialog = false
          await this.fetchDetail()
        }
      } finally {
        this.feeDeleteLoading = false
      }
    },

    /* ================================================================
     * 지점 삭제 (HQ 전용)
     * ================================================================ */

    async handleBranchDelete() {
      this.branchDeleteLoading = true
      try {
        const res = await deleteBranch(this.branchCode)
        if (res.data.code === 0) {
          this.$router.push('/branches')
        }
      } finally {
        this.branchDeleteLoading = false
      }
    },

    /* ================================================================
     * 화면 유틸
     * ================================================================ */

    getBranchTypeLabel(type) {
      const map = { HQ: '본사', DIRECT: '직영점', AGENCY: '대리점', FRANCHISE: '가맹점' }
      return map[type] || type
    },

    getBranchTypeColor(type) {
      const map = { HQ: 'purple', DIRECT: 'primary', AGENCY: 'info', FRANCHISE: 'success' }
      return map[type] || 'default'
    },

    getBranchStatusLabel(status) {
      const map = { ACTIVE: '운영중', INACTIVE: '일시중지', CLOSED: '폐점' }
      return map[status] || status
    },

    getBranchStatusColor(status) {
      const map = { ACTIVE: 'success', INACTIVE: 'warning', CLOSED: 'error' }
      return map[status] || 'default'
    },

    formatFeeRate(rate) {
      if (rate == null) return '-'
      return (parseFloat(rate) * 100).toFixed(4) + '%'
    },

    formatDateTime(dt) {
      if (!dt) return '-'
      return dt.replace('T', ' ').slice(0, 16)
    },

    /** 사업자번호 000-00-00000 형식으로 포맷 (숫자만 추출 후 적용) */
    formatBranchNo(value) {
      const digits = (value || '').replace(/\D/g, '').slice(0, 10)
      if (digits.length <= 3) return digits
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
    },
  },
}
</script>

<template>
  <div class="branch-detail-page">

    <!-- 로딩 -->
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 400px">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>

    <template v-else-if="detail">

      <!-- ===== 페이지 헤더 (감정평가 상세와 동일 형식) ===== -->
      <div class="mb-5">
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="breadcrumb-link text-primary" @click="$router.push('/branches')">지점관리</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>상세</span>
        </div>
        <div class="text-h5 font-weight-bold">지점관리 상세</div>
        <div class="text-body-2 text-grey mt-1">지점 정보 조회 및 수정</div>
      </div>

      <!-- ===== 히어로 카드 ===== -->
      <div class="branch-hero mb-7">
        <div class="hero-card">
          <div class="hero-bg pa-6">
            <!-- 장식용 배경 원형 요소 -->
            <div class="hero-deco-circle hero-deco-1" />
            <div class="hero-deco-circle hero-deco-2" />
            <div class="hero-deco-circle hero-deco-3" />

            <div class="d-flex align-start justify-space-between hero-content">
              <div class="flex-grow-1">
                <div class="d-flex align-center ga-2 mb-3 flex-wrap">
                  <v-chip
                    :color="getBranchTypeColor(detail.branchType)"
                    size="small"
                    variant="elevated"
                  >
                    {{ getBranchTypeLabel(detail.branchType) }}
                  </v-chip>
                  <v-chip
                    :color="getBranchStatusColor(detail.branchStatus)"
                    size="small"
                    variant="flat"
                  >
                    {{ getBranchStatusLabel(detail.branchStatus) }}
                  </v-chip>
                </div>
                <h1 class="text-h4 font-weight-bold text-white mb-2">{{ detail.branchName }}</h1>
                <div class="d-flex align-center ga-1" style="color: rgba(255,255,255,0.72)">
                  <v-icon size="14" color="white" style="opacity: 0.65">mdi-map-marker-outline</v-icon>
                  <span class="text-body-2">{{ detail.address1 || '주소 미등록' }}</span>
                  <span v-if="detail.address2" class="text-body-2"> · {{ detail.address2 }}</span>
                </div>
              </div>
              <div class="hero-icon-wrap ml-4">
                <v-icon size="34" color="white">mdi-store-outline</v-icon>
              </div>
            </div>

            <v-divider class="my-5 hero-content" style="border-color: rgba(255,255,255,0.18)" />

            <div class="d-flex ga-3 flex-wrap hero-content">
              <div class="hero-stat">
                <div class="hero-stat-icon">
                  <v-icon size="16" color="white">mdi-account-multiple-outline</v-icon>
                </div>
                <div>
                  <div class="hero-stat-label">관리자</div>
                  <div class="hero-stat-value">{{ detail.admins?.length || 0 }}명</div>
                </div>
              </div>
              <div class="hero-stat">
                <div class="hero-stat-icon">
                  <v-icon size="16" color="white">mdi-percent-outline</v-icon>
                </div>
                <div>
                  <div class="hero-stat-label">수수료 정책</div>
                  <div class="hero-stat-value">{{ detail.feePolicies?.length || 0 }}건</div>
                </div>
              </div>
              <div v-if="detail.branchCode" class="hero-stat">
                <div class="hero-stat-icon">
                  <v-icon size="16" color="white">mdi-identifier</v-icon>
                </div>
                <div>
                  <div class="hero-stat-label">지점코드</div>
                  <div class="hero-stat-value" style="font-family: monospace; letter-spacing: 0.05em">
                    {{ detail.branchCode }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== 탭 컨테이너 ===== -->
      <v-card rounded="lg" elevation="0" border class="mb-5">

        <!-- 탭 헤더 -->
        <v-tabs v-model="activeTab" color="primary" class="branch-tabs">
          <v-tab value="info">
            <v-icon start size="16">mdi-store-edit-outline</v-icon>
            기본정보
          </v-tab>
          <v-tab value="admins">
            <v-icon start size="16">mdi-account-multiple-outline</v-icon>
            지점관리자
            <v-chip
              v-if="detail.admins?.length"
              size="x-small"
              variant="tonal"
              color="indigo"
              class="ml-2"
            >{{ detail.admins.length }}</v-chip>
          </v-tab>
          <v-tab value="fees">
            <v-icon start size="16">mdi-percent-outline</v-icon>
            수수료 정책
            <v-chip
              v-if="detail.feePolicies?.length"
              size="x-small"
              variant="tonal"
              color="teal"
              class="ml-2"
            >{{ detail.feePolicies.length }}</v-chip>
          </v-tab>
        </v-tabs>

        <v-divider />

        <v-window v-model="activeTab" transition="none" reverse-transition="none" class="tab-window">

          <!-- ── Tab 1: 기본정보 ── -->
          <v-window-item value="info">
            <div class="info-form-wrap">

              <v-alert
                v-if="branchSaveMsg"
                type="success"
                variant="tonal"
                density="compact"
                rounded="lg"
                class="mb-5"
              >{{ branchSaveMsg }}</v-alert>
              <v-alert
                v-if="branchSaveError"
                type="error"
                variant="tonal"
                density="compact"
                rounded="lg"
                class="mb-5"
              >{{ branchSaveError }}</v-alert>

              <!-- 기본 정보 그룹 -->
              <div class="form-group-label">기본 정보</div>
              <v-row>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="branchForm.branchName"
                    label="지점명 *"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details="auto"
                    :readonly="!isHq"
                    :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    :model-value="branchForm.branchNo"
                    label="사업자번호"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    placeholder="000-00-00000"
                    :readonly="!isHq"
                    :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                    @update:model-value="isHq && (branchForm.branchNo = formatBranchNo($event))"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="branchForm.branchType"
                    :items="branchTypeOptions"
                    item-title="title"
                    item-value="value"
                    label="지점구분"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    :readonly="!isHq"
                    :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="branchForm.branchStatus"
                    :items="branchStatusOptions"
                    item-title="title"
                    item-value="value"
                    label="운영상태"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    :readonly="!isHq"
                    :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                  />
                </v-col>
              </v-row>

              <v-divider class="my-5" />

              <!-- 주소 그룹 -->
              <div class="form-group-label">주소 정보</div>
              <v-row>
                <v-col cols="12">
                  <div class="d-flex align-center ga-3">
                    <v-text-field
                      v-model="branchForm.zipcode"
                      label="우편번호"
                      variant="outlined"
                      density="comfortable"
                      rounded="lg"
                      hide-details
                      readonly
                      placeholder="주소 찾기"
                      :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                      style="max-width: 180px; flex-shrink: 0"
                    />
                    <!-- 주소 찾기 버튼: HQ만 표시 -->
                    <v-btn
                      v-if="isHq"
                      color="primary"
                      variant="tonal"
                      rounded="lg"
                      prepend-icon="mdi-map-search-outline"
                      height="48"
                      @click="openAddressSearch"
                    >주소 찾기</v-btn>
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="branchForm.address1"
                    label="도로명 주소"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    readonly
                    :placeholder="isHq ? '위 주소 찾기 버튼을 이용해 주세요' : ''"
                    :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="branchForm.address2"
                    label="상세 주소"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    :readonly="!isHq"
                    :placeholder="isHq ? '층, 호수 등 상세 주소 입력' : ''"
                    :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                  />
                </v-col>
              </v-row>

              <v-divider class="my-5" />

              <!-- 좌표 그룹 -->
              <div class="form-group-label">좌표 <span class="form-group-hint">주소 선택 시 자동 입력</span></div>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="branchForm.locationX"
                    label="경도 (X)"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    readonly
                    placeholder="자동 입력"
                    :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="branchForm.locationY"
                    label="위도 (Y)"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    readonly
                    placeholder="자동 입력"
                    :bg-color="!isHq ? 'grey-lighten-4' : undefined"
                  />
                </v-col>
              </v-row>

            </div>

            <!-- 저장/되돌리기 버튼: HQ 관리자만 표시 -->
            <template v-if="isHq">
              <v-divider />
              <div class="d-flex justify-end pa-4 ga-2">
                <v-btn variant="text" :disabled="branchSaving" @click="initBranchForm">
                  되돌리기
                </v-btn>
                <v-btn color="primary" variant="flat" rounded="lg" :loading="branchSaving" @click="handleBranchSave">
                  <v-icon start size="18">mdi-content-save-outline</v-icon>
                  저장
                </v-btn>
              </div>
            </template>
          </v-window-item>

          <!-- ── Tab 2: 지점관리자 ── -->
          <v-window-item value="admins">
            <div class="d-flex align-center justify-end px-5 py-3 tab-action-bar">
              <v-btn
                size="small"
                color="indigo"
                variant="tonal"
                prepend-icon="mdi-plus"
                rounded="lg"
                @click="openAdminCreate"
              >관리자 추가</v-btn>
            </div>
            <v-divider />

            <div v-if="!detail.admins?.length" class="tab-empty">
              <v-icon size="40" color="grey-lighten-2" class="mb-3">mdi-account-off-outline</v-icon>
              <div class="text-body-2 text-medium-emphasis">등록된 관리자가 없습니다.</div>
            </div>

            <v-table v-else density="comfortable" class="admin-table">
              <thead>
                <tr>
                  <th class="text-caption font-weight-bold">이름</th>
                  <th class="text-caption font-weight-bold">이메일</th>
                  <th class="text-caption font-weight-bold">직책</th>
                  <th class="text-caption font-weight-bold">전화번호</th>
                  <th class="text-caption font-weight-bold text-center" style="width: 180px">관리</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="admin in detail.admins" :key="admin.branchAdminCode">
                  <td>
                    <div class="d-flex align-center ga-2">
                      <v-avatar size="28" color="indigo-lighten-4" rounded="lg">
                        <span class="text-caption font-weight-bold text-indigo">
                          {{ admin.branchAdminName?.charAt(0) }}
                        </span>
                      </v-avatar>
                      <span class="font-weight-medium text-body-2">{{ admin.branchAdminName }}</span>
                    </div>
                  </td>
                  <td class="text-caption text-medium-emphasis">{{ admin.branchAdminEmail }}</td>
                  <td>
                    <v-chip v-if="admin.branchAdminPosition" size="x-small" variant="tonal" color="indigo" label>
                      {{ admin.branchAdminPosition }}
                    </v-chip>
                    <span v-else class="text-caption text-disabled">—</span>
                  </td>
                  <td class="text-caption text-medium-emphasis">{{ admin.branchAdminPhone || '—' }}</td>
                  <td class="text-center">
                    <v-btn size="x-small" variant="text" color="primary" class="mr-1" @click="openAdminEdit(admin)">수정</v-btn>
                    <!-- 비밀번호 변경: HQ 및 지점관리자 모두 가능 -->
                    <v-btn size="x-small" variant="text" color="indigo" class="mr-1" @click="openPwdChange(admin)">비밀번호</v-btn>
                    <v-btn size="x-small" variant="text" color="error" @click="openAdminDelete(admin)">삭제</v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-window-item>

          <!-- ── Tab 3: 수수료 정책 ── -->
          <v-window-item value="fees">
            <div class="d-flex align-center justify-space-between px-5 py-3 tab-action-bar">
              <!-- 지점관리자 조회 전용 안내 -->
              <span v-if="!isHq" class="text-caption text-medium-emphasis d-flex align-center ga-1">
                <v-icon size="14" color="grey">mdi-lock-outline</v-icon>
                수수료 정책 추가/수정/삭제는 본사 관리자만 가능합니다.
              </span>
              <span v-else />
              <!-- 정책 추가 버튼: HQ만 표시 -->
              <v-btn
                v-if="isHq"
                size="small"
                color="teal"
                variant="tonal"
                prepend-icon="mdi-plus"
                rounded="lg"
                @click="openFeeCreate"
              >정책 추가</v-btn>
            </div>
            <v-divider />

            <div v-if="!detail.feePolicies?.length" class="tab-empty">
              <v-icon size="40" color="grey-lighten-2" class="mb-3">mdi-percent-box-outline</v-icon>
              <div class="text-body-2 text-medium-emphasis">등록된 수수료 정책이 없습니다.</div>
            </div>

            <v-table v-else density="comfortable" class="fee-table">
              <thead>
                <tr>
                  <th class="text-caption font-weight-bold">매수 수수료</th>
                  <th class="text-caption font-weight-bold">매도 수수료</th>
                  <th class="text-caption font-weight-bold">적용 시작</th>
                  <th class="text-caption font-weight-bold">적용 종료</th>
                  <!-- 관리 컬럼 헤더: HQ만 표시 -->
                  <th v-if="isHq" class="text-caption font-weight-bold text-center" style="width: 120px">관리</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="policy in detail.feePolicies" :key="policy.policyId">
                  <td>
                    <span class="font-weight-medium text-body-2">{{ formatFeeRate(policy.buyFeeRate) }}</span>
                  </td>
                  <td>
                    <span class="font-weight-medium text-body-2">{{ formatFeeRate(policy.sellFeeRate) }}</span>
                  </td>
                  <td class="text-caption text-medium-emphasis">{{ formatDateTime(policy.startDate) }}</td>
                  <td class="text-caption text-medium-emphasis">{{ policy.endDate ? formatDateTime(policy.endDate) : '—' }}</td>
                  <!-- 수정/삭제 버튼: HQ만 표시, 지점관리자는 열 자체를 숨김 -->
                  <td v-if="isHq" class="text-center">
                    <v-btn size="x-small" variant="text" color="primary" class="mr-1" @click="openFeeEdit(policy)">수정</v-btn>
                    <v-btn size="x-small" variant="text" color="error" @click="openFeeDelete(policy)">삭제</v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-window-item>

        </v-window>
      </v-card>

      <!-- ===== 위험 구역: 지점 삭제 (HQ 전용) ===== -->
      <v-card v-if="isHq" rounded="lg" elevation="0" border class="danger-zone">
        <div class="d-flex align-center ga-3 px-5 py-4">
          <v-icon size="20" color="error">mdi-alert-outline</v-icon>
          <div>
            <div class="text-subtitle-2 font-weight-bold text-error">위험 구역</div>
            <div class="text-caption text-medium-emphasis">이 작업은 되돌릴 수 없습니다.</div>
          </div>
          <v-spacer />
          <v-btn
            color="error"
            variant="outlined"
            rounded="lg"
            prepend-icon="mdi-delete-outline"
            @click="branchDeleteDialog = true"
          >
            지점 삭제
          </v-btn>
        </div>
      </v-card>

    </template>

    <!-- ====================================================
         다이얼로그 모음
         ==================================================== -->

    <!-- 관리자 등록/수정 다이얼로그 -->
    <BranchAdminDialog
      v-if="detail"
      v-model="adminDialog"
      :mode="adminFormMode"
      :branch-code="branchCode"
      :admin="selectedAdmin"
      @saved="onAdminSaved"
    />

    <!-- 관리자 삭제 확인 -->
    <v-dialog v-model="adminDeleteDialog" max-width="380">
      <v-card rounded="lg">
        <v-card-item class="pa-5 pb-3">
          <template #prepend>
            <v-icon color="error">mdi-account-remove-outline</v-icon>
          </template>
          <v-card-title class="text-subtitle-1 font-weight-bold">관리자 삭제</v-card-title>
        </v-card-item>
        <v-card-text class="px-5 pb-3">
          <span class="font-weight-bold">{{ adminToDelete?.branchAdminName }}</span> 관리자를 삭제하시겠습니까?
          <div class="text-caption text-medium-emphasis mt-1">삭제된 계정은 로그인이 불가능합니다.</div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" rounded="lg" :disabled="adminDeleteLoading" @click="adminDeleteDialog = false">취소</v-btn>
          <v-btn color="error" variant="flat" rounded="lg" :loading="adminDeleteLoading" @click="handleAdminDelete">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 관리자 비밀번호 변경 다이얼로그 -->
    <v-dialog v-model="pwdDialog" max-width="420" persistent>
      <v-card rounded="lg">
        <v-card-item class="pa-5 pb-3">
          <template #prepend>
            <v-icon color="indigo">mdi-lock-reset</v-icon>
          </template>
          <v-card-title class="text-subtitle-1 font-weight-bold">비밀번호 변경</v-card-title>
          <v-card-subtitle v-if="pwdTarget">{{ pwdTarget.branchAdminName }} 관리자</v-card-subtitle>
        </v-card-item>
        <v-divider />
        <v-card-text class="pa-5">
          <v-alert v-if="pwdMsg" type="success" variant="tonal" density="compact" rounded="lg" class="mb-4">
            {{ pwdMsg }}
          </v-alert>
          <v-alert v-if="pwdError" type="error" variant="tonal" density="compact" rounded="lg" class="mb-4">
            {{ pwdError }}
          </v-alert>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="pwdForm.newPassword"
                label="새 비밀번호 *"
                type="password"
                variant="outlined"
                density="compact"
                hint="8자 이상 입력해 주세요"
                persistent-hint
                autocomplete="new-password"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="pwdForm.confirmPassword"
                label="새 비밀번호 확인 *"
                type="password"
                variant="outlined"
                density="compact"
                hide-details
                autocomplete="new-password"
                @keyup.enter="handlePwdSave"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" rounded="lg" :disabled="pwdSaving" @click="pwdDialog = false">취소</v-btn>
          <v-btn color="indigo" variant="flat" rounded="lg" :loading="pwdSaving" @click="handlePwdSave">변경</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 수수료 정책 등록/수정 다이얼로그 -->
    <v-dialog v-model="feeDialog" max-width="460" persistent>
      <v-card rounded="lg">
        <v-card-item class="pa-5 pb-3">
          <template #prepend>
            <v-icon color="teal">mdi-percent-outline</v-icon>
          </template>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ feeFormMode === 'create' ? '수수료 정책 등록' : '수수료 정책 수정' }}
          </v-card-title>
        </v-card-item>
        <v-divider />
        <v-card-text class="pa-5">
          <v-alert
            v-if="feeSaveError"
            type="error"
            variant="tonal"
            density="compact"
            rounded="lg"
            class="mb-4"
          >
            {{ feeSaveError }}
          </v-alert>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="feeForm.buyFeeRate"
                label="매수 수수료율 *"
                variant="outlined"
                density="compact"
                type="number"
                step="0.000001"
                hint="예: 0.003000 = 0.3%"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="feeForm.sellFeeRate"
                label="매도 수수료율"
                variant="outlined"
                density="compact"
                type="number"
                step="0.000001"
                hint="예: 0.005000 = 0.5%"
                persistent-hint
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="feeForm.startDate"
                label="적용 시작일시 *"
                variant="outlined"
                density="compact"
                type="datetime-local"
                hide-details
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="feeForm.endDate"
                label="적용 종료일시 (비워두면 현재 적용 중)"
                variant="outlined"
                density="compact"
                type="datetime-local"
                hide-details
                clearable
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" rounded="lg" :disabled="feeSaving" @click="feeDialog = false">취소</v-btn>
          <v-btn color="teal" variant="flat" rounded="lg" :loading="feeSaving" @click="handleFeeSave">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 수수료 정책 삭제 확인 -->
    <v-dialog v-model="feeDeleteDialog" max-width="380">
      <v-card rounded="lg">
        <v-card-item class="pa-5 pb-3">
          <template #prepend>
            <v-icon color="error">mdi-percent-remove-outline</v-icon>
          </template>
          <v-card-title class="text-subtitle-1 font-weight-bold">수수료 정책 삭제</v-card-title>
        </v-card-item>
        <v-card-text class="px-5 pb-3">
          매수 <strong>{{ formatFeeRate(feeToDelete?.buyFeeRate) }}</strong> /
          매도 <strong>{{ formatFeeRate(feeToDelete?.sellFeeRate) }}</strong> 정책을 삭제하시겠습니까?
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" rounded="lg" :disabled="feeDeleteLoading" @click="feeDeleteDialog = false">취소</v-btn>
          <v-btn color="error" variant="flat" rounded="lg" :loading="feeDeleteLoading" @click="handleFeeDelete">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 지점 삭제 확인 -->
    <v-dialog v-model="branchDeleteDialog" max-width="400">
      <v-card rounded="lg">
        <v-card-item class="pa-5 pb-3">
          <template #prepend>
            <v-icon color="error">mdi-store-remove-outline</v-icon>
          </template>
          <v-card-title class="text-subtitle-1 font-weight-bold">지점 삭제</v-card-title>
        </v-card-item>
        <v-card-text class="px-5 pb-3">
          <strong>{{ detail?.branchName }}</strong> 지점을 삭제하시겠습니까?
          <div class="text-caption text-error mt-2">
            <v-icon size="14" class="mr-1">mdi-alert</v-icon>
            소속 관리자 계정도 함께 비활성화됩니다. 이 작업은 되돌릴 수 없습니다.
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" rounded="lg" :disabled="branchDeleteLoading" @click="branchDeleteDialog = false">취소</v-btn>
          <v-btn color="error" variant="flat" rounded="lg" :loading="branchDeleteLoading" @click="handleBranchDelete">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<style scoped>
/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* 히어로 헤더 */
.hero-card {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.hero-bg {
  background: linear-gradient(135deg, #8a6515 0%, #b5891e 35%, #c9a040 65%, #d4aa4e 100%);
  position: relative;
  overflow: hidden;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-deco-circle {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}

.hero-deco-1 {
  width: 220px;
  height: 220px;
  top: -90px;
  right: -60px;
  background: rgba(255, 255, 255, 0.08);
}

.hero-deco-2 {
  width: 140px;
  height: 140px;
  bottom: -60px;
  right: 160px;
  background: rgba(255, 255, 255, 0.05);
}

.hero-deco-3 {
  width: 80px;
  height: 80px;
  top: 20px;
  right: 80px;
  background: rgba(255, 255, 255, 0.06);
}

.hero-icon-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 1;
}

.hero-stat {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px 14px;
  backdrop-filter: blur(4px);
}

.hero-stat-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hero-stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.3;
}

.hero-stat-value {
  font-size: 13px;
  font-weight: 600;
  color: white;
  line-height: 1.4;
}

/* 탭 */
.branch-tabs :deep(.v-tab) {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  min-width: 0;
  padding: 0 20px;
  height: 48px;
}

.branch-tabs :deep(.v-tab--selected) {
  font-weight: 700;
}

.tab-window {
  min-height: 600px;
}

/* ── 기본정보 폼 ── */
.info-form-wrap {
  padding: 32px 32px 16px;
}

.info-form-wrap :deep(.v-row) {
  row-gap: 10px;
}

.info-form-wrap :deep(.v-field) {
  min-height: 48px;
}

.info-form-wrap :deep(.v-field__input) {
  min-height: 48px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.form-group-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.38);
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-group-hint {
  font-size: 0.68rem;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  color: rgba(0, 0, 0, 0.32);
}

.tab-action-bar {
  background-color: #fafafa;
  min-height: 60px;
}

.tab-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 52px 16px;
}

/* 테이블 공통 */
.admin-table :deep(thead th),
.fee-table :deep(thead th) {
  background-color: #fafafa;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}

.admin-table :deep(tbody tr:hover),
.fee-table :deep(tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.02);
}

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* 위험 구역 */
.danger-zone {
  border-color: rgba(var(--v-theme-error), 0.3) !important;
}

@media (max-width: 959px) {
  .info-form-wrap {
    padding: 24px 18px 12px;
  }
}
</style>
